import { cn } from "@repo/design-system/utils";
import { Buildings } from "@medusajs/icons";
import { memo } from "react";

/**
 * Joins supplier names into a readable list: "A", "A and B", "A, B and C".
 * Exported for the test that pins the copy.
 */
export function joinSupplierNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "Your supplier";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export interface SupplierCollectedInstructionsProps {
  /**
   * The distinct supplier names in this checkout. All bill the salon directly;
   * a combined checkout only ever groups suppliers of this one mode (the mixed
   * case is blocked before checkout), so this is one name or several of the
   * same kind.
   */
  sellerNames: readonly string[];
  className?: string;
}

/**
 * The payment panel for a supplier-collected checkout (KAN-131).
 *
 * Replaces the card form entirely: these suppliers keep the billing
 * relationship with the salon, so there is nothing to collect here and no payee
 * details to show. The copy is fixed and generic - there is no per-seller
 * instructions field - and it deliberately does not imply SalonStocker bills the
 * order or its tax, since the supplier is the merchant of record.
 */
export const SupplierCollectedInstructions = memo(
  function SupplierCollectedInstructions({
    sellerNames,
    className
  }: SupplierCollectedInstructionsProps) {
    const subject = joinSupplierNames(sellerNames);
    const verb = sellerNames.length > 1 ? "collect" : "collects";

    return (
      <div
        className={cn(
          "rounded-lg border border-gray-200 bg-gray-50 px-4 py-3",
          className
        )}
      >
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <Buildings aria-hidden="true" />
          <span>Payment handled by your supplier</span>
        </div>

        <p className="mt-1.5 text-sm text-gray-600">
          {subject} {verb} payment directly and will contact you with payment
          instructions after you place this order.
        </p>
      </div>
    );
  }
);
